import { Alert, Button, Modal } from '@orbita-ui/core';
import { useAuth } from 'auth/hooks/useAuth';
import { IRefundForm, RefundsForm } from 'core/cuenta/components/RefundsForm/RefundsForm';
import { RefundsInfo } from 'core/cuenta/components/RefundsInfo/RefundsInfo';
import { TransfersTable } from 'core/cuenta/components/TransfersTable/TransfersTable';
import { RefundsContext } from 'core/cuenta/contexts/RefundsContext';
import { Transferencia } from 'core/cuenta/models/Transferencia';
import { ACCOUNT_TRANSFER_HISTORY_PATH } from 'core/cuenta/routes';
import { useCoreService } from 'core/services/useCoreService';
import { useContext, useState } from 'react';
import { Col, Container, Row } from 'react-bootstrap';
import { NavLink } from 'react-router-dom';
import { Card } from 'shared/components';
import { PageTitle, SectionTitle } from 'shared/components/Fonts';
import { useFileDownloader } from 'shared/hooks/useFileDownloader';
import { BaseModal, useModal } from 'shared/modal';
import { formatCurrency } from 'shared/utils';
import { useImpersonationGuard } from 'auth/hooks/useImpersonationGuard';

export const RefundsPage = () => {
  const { user } = useAuth();
  const { isLoading, refundData, error, doDevolucion, isPartialRefund } = useContext(RefundsContext);
  const { showModal, closeModal, openModal } = useModal(false);
  const coreService = useCoreService();
  const { downloadFile } = useFileDownloader();

  const [currentRefund, setCurrentRefund] = useState<number>(null);
  const { guardWriteAction } = useImpersonationGuard();


  const doSubmit = () => {
    if (!guardWriteAction()) {
      closeModal();
      return;
    }
    doDevolucion(currentRefund);
    closeModal();
  };

  const showConfirmModal = (data: IRefundForm) => {
    if (!guardWriteAction()) {
      return;
    }
    setCurrentRefund(data.refund);
    openModal();
  };

  const canSeeMoreTransfers = () => {
    if (!user?.funcionalidades) return false;

    return user.funcionalidades.some((funcionalidad) =>
      ['DISTRIBUCION_UNITARIA_CONSULTA', 'DEVOLUCION_TOTAL_CONSULTA', 'DEVOLUCION_PARCIAL_CONSULTA'].includes(funcionalidad)
    );
  };

  const onDownloadComprobante = async (transfer: Transferencia) => {
    const filePayload = await coreService.downloadTransferComprobante(transfer.id);
    downloadFile(filePayload, filePayload.name);
  };


  return (
    <>
      <Container fluid>
        <Row className="mb-3">
          <PageTitle>Devolución de fondos</PageTitle>
        </Row>
        {(error?.apiError?.message === 'card_without_cc') ? (
          <Row className="mb-3">
            <Col>
              <Alert
                variant="error"
                subtitle="La tarjeta no puede realizar devoluciones porque no tiene un centro de costos asociado"
                fullWidth
              />
            </Col>
          </Row>
        ) : (
          <>

            <Row className="mb-3">
              <Col>
                <RefundsInfo refundData={isLoading ? null : refundData} />
              </Col>
            </Row>
            <Row className="mb-3">
              <Col>
                <RefundsForm
                  refundData={isLoading ? null : refundData}
                  onSubmit={showConfirmModal}
                  partialRefund={isPartialRefund}
                />
              </Col>
            </Row>
            <Row className="mb-3">
              <Col>
                <SectionTitle>Últimas Transferencias</SectionTitle>
              </Col>
            </Row>
            <Row>
              <Col>
                <Card>
                  <Card.Body>
                    <Row>
                      <Col className="pt-32">
                        <TransfersTable
                          tableContent={isLoading ? null : refundData?.transferencias}
                          onDownloadComprobante={onDownloadComprobante}
                        />
                        {!isLoading && !refundData?.transferencias?.length && (
                          <div className="p-3">
                            <Alert
                              variant="info"
                              fullWidth
                              subtitle="No realizaste transferencias en el último mes"
                            />
                          </div>
                        )}
                      </Col>
                    </Row>
                  </Card.Body>
                  <Card.Footer>
                    {canSeeMoreTransfers() && refundData && (
                      <NavLink to={ACCOUNT_TRANSFER_HISTORY_PATH}>
                        <Button>Ver más...</Button>
                      </NavLink>
                    )}
                  </Card.Footer>
                </Card>
              </Col>
            </Row>
          </>
        )}

      </Container>
      <Modal show={showModal} onClose={closeModal}>
        <BaseModal
          title={<div>Realizar devolución</div>}
          body={
            <>
              <p>¿Está seguro que desea realizar esta devolución?</p>
              <p>Va a realizar una devolución de {formatCurrency(currentRefund)}</p>
            </>
          }
          footer={
            <>
              <Button color="destructive" variant="outline" onClick={() => closeModal()}>
                Cancelar
              </Button>
              <Button onClick={doSubmit}>Aceptar</Button>
            </>
          }
        />
      </Modal>
    </>
  );
};
